/**
 * Dokumente — Cockpit-Bereich mit Tab-Leiste (TQ #337, Auftrag Kim/Patric 25.09.2026).
 *
 * Anders als "Einstellungen" (Settings.tsx, ?tab=-Query-Param) braucht Dokumente
 * ECHTE Routen pro Tab, damit Links aus Mails/Chat direkt dorthin führen
 * ("jeder Tab mit eigener Route"). Deshalb nested <Routes> statt Query-Param-Tabs.
 *
 * Angebote + Rechnungen ziehen aus den bisherigen eigenstaendigen Seiten
 * (Quotations.tsx / Billing.tsx-Rechnungsabschnitt) hierher um — siehe
 * App.tsx (billing/quotations sind jetzt Redirects hierher) und Layout.tsx
 * (ein Menüpunkt "Dokumente" statt getrennter "Abrechnung"/"Angebote"-Einträge).
 */
import { NavLink, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import Quotations from './Quotations'
import InvoicesSection from '../components/InvoicesSection'
import Contracts from './documents/Contracts'
import Reports from './documents/Reports'
import ReportFeedbackActions from '../components/ReportFeedbackActions'
import { isTenantInstance } from '../lib/runtimeConfig'
import './Documents.css'

interface Organization {
  id: number
  name: string
  slug: string
  public_token?: string
  member_role: string
}

interface Props {
  organization: Organization
}

const TABS = [
  // TQ #337 (Backend-Abgleich 30.09.2026, it-sce-platform org_users.go):
  // Angebote ("/quotations") ist entgegen der urspruenglichen Annahme ebenfalls
  // owner/admin-only ("Owner-only fuer Annahme, Liste/Detail/PDF Owner+Admin").
  // Nur Rechnungen ("/erpnext/invoices") liegt im memberOnly-Tier — jedes
  // Mitglied darf die Rechnungen seiner Org sehen.
  { path: 'angebote', label: 'Angebote', adminOnly: true },
  { path: 'rechnungen', label: 'Rechnungen', adminOnly: false },
  { path: 'vertraege', label: 'Verträge', adminOnly: true },
  { path: 'regieberichte', label: 'Regieberichte', adminOnly: true },
] as const

// Die Token-Historie ist in die Abrechnung gezogen (Spec Informationsarchitektur, Patric 03.10.2026).
// Alte Links und Lesezeichen auf .../dokumente/token-historie leiten dorthin um.
function TokenHistoryMoved() {
  const { pathname } = useLocation()
  return <Navigate to={pathname.replace(/\/dokumente\/token-historie\/?$/, '/billing') + '#token-verlauf'} replace />
}

export default function Documents({ organization }: Props) {
  // TQ #337: Vier der fuenf Tabs liegen backend-seitig im admin-Tier
  // (services/portal-api org_users.go: admin.GET("/quotations"|"/documents/...")),
  // nur Rechnungen ist memberOnly. Fuer normale Mitglieder werden die
  // admin-only-Tabs deshalb weder in der Leiste angezeigt noch geroutet — sonst
  // laeuft man dort nur in einen 403. Default-Tab folgt daraus rollenabhaengig
  // (Owner/Admin -> Angebote, Member -> Rechnungen als einziger sichtbarer Tab).
  const isOwner = organization.member_role === 'owner'
  const isAdminOrOwner = organization.member_role === 'owner' || organization.member_role === 'admin'
  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdminOrOwner)
  const defaultTab = visibleTabs[0]?.path ?? 'rechnungen'

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Dokumente</h1>
          <p className="page-subtitle">
            Angebote, Rechnungen, Verträge und Regieberichte an einem Ort.
          </p>
        </div>
      </div>

      <nav className="documents-tabbar" aria-label="Dokumente">
        {visibleTabs.map((t) => (
          <NavLink
            key={t.path}
            to={t.path}
            className={({ isActive }) => `documents-tab-btn ${isActive ? 'active' : ''}`}
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <div className="documents-tab-content">
        <Routes>
          <Route index element={<Navigate to={defaultTab} replace />} />
          {isAdminOrOwner && (
            <Route path="angebote" element={<Quotations organization={organization} />} />
          )}
          <Route
            path="rechnungen"
            element={<InvoicesSection organization={organization} personal={false} />}
          />
          {isAdminOrOwner && (
            <>
              <Route path="vertraege" element={<Contracts organization={organization} />} />
              <Route path="regieberichte" element={
                <Reports
                  organization={organization}
                  renderReportActions={isOwner && isTenantInstance()
                    ? (r, { reload }) => (
                      <ReportFeedbackActions report={r} slugOrToken={organization.public_token || organization.slug} onDone={reload} />
                    )
                    : undefined}
                />
              } />
              <Route path="token-historie" element={<TokenHistoryMoved />} />
            </>
          )}
          <Route path="*" element={<Navigate to={defaultTab} replace />} />
        </Routes>
      </div>
    </div>
  )
}
